/**
 * Size Guide Modal
 * Pannello laterale per la guida alle taglie
 */

class SizeGuideModal {
  constructor() {
    this.modal = null;
    this.overlay = null;
    this.isOpen = false;
    this.isInches = false; // false = mm, true = inches
    this.focusableElements = [];
    this.firstFocusable = null;
    this.lastFocusable = null;
    this.previousActiveElement = null;

    this.init();
  }

  init() {
    this.modal = document.getElementById('size-guide-modal');
    this.overlay = document.getElementById('size-guide-overlay');

    if (!this.modal || !this.overlay) return;

    this.bindEvents();
  }

  bindEvents() {
    // Usa event delegation sul document per catturare tutti i trigger (anche quelli aggiunti dinamicamente)
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-modal-open="size-guide-modal"]');
      if (trigger) {
        e.preventDefault();
        e.stopPropagation();
        this.open();
      }
    });

    // Chiusura modal da bottone close
    const closeBtn = this.modal.querySelector('[data-modal-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }

    // Chiusura con click su overlay
    this.overlay.addEventListener('click', () => this.close());

    // Chiusura con ESC
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Trap focus dentro modal
    this.modal.addEventListener('keydown', (e) => this.trapFocus(e));

    // Toggle mm/inches
    const unitToggle = this.modal.querySelector('[data-unit-toggle]');
    if (unitToggle) {
      unitToggle.addEventListener('click', () => this.toggleUnits());
    }
  }

  toggleUnits() {
    this.isInches = !this.isInches;
    
    const toggleMm = this.modal.querySelector('[data-toggle-mm]');
    const toggleInches = this.modal.querySelector('[data-toggle-inches]');
    
    // Switch SVG visibility
    if (toggleMm && toggleInches) {
      if (this.isInches) {
        toggleMm.classList.add('tw-hidden');
        toggleInches.classList.remove('tw-hidden');
      } else {
        toggleMm.classList.remove('tw-hidden');
        toggleInches.classList.add('tw-hidden');
      }
    }

    // Mostra/nascondi valori mm/inches
    const mmElements = this.modal.querySelectorAll('[data-size-mm], [data-measurement$="-mm"]');
    const inchElements = this.modal.querySelectorAll('[data-size-inches], [data-measurement$="-inches"]');

    mmElements.forEach(el => {
      el.classList.toggle('tw-hidden', this.isInches);
    });

    inchElements.forEach(el => {
      el.classList.toggle('tw-hidden', !this.isInches);
    });
  }

  open() {
    if (this.isOpen) return;

    this.previousActiveElement = document.activeElement;
    this.isOpen = true;

    // Mostra overlay e modal
    this.overlay.classList.remove('tw-opacity-0', 'tw-pointer-events-none');
    this.overlay.classList.add('tw-opacity-100', 'tw-pointer-events-auto');
    this.modal.classList.remove('size-guide-panel--closed', 'tw-invisible');
    this.modal.classList.add('size-guide-panel--open');

    // Blocca scroll body
    document.body.style.overflow = 'hidden';

    // Nascondi chat Genesys e sticky cart
    this.hideOverlappingElements();

    // Setup focus trap
    this.setupFocusTrap();

    // Focus sul primo elemento focusabile
    if (this.firstFocusable) {
      this.firstFocusable.focus();
    }
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;

    // Nascondi modal e overlay
    this.modal.classList.remove('size-guide-panel--open');
    this.modal.classList.add('size-guide-panel--closed', 'tw-invisible');
    this.overlay.classList.remove('tw-opacity-100', 'tw-pointer-events-auto');
    this.overlay.classList.add('tw-opacity-0', 'tw-pointer-events-none');

    // Ripristina scroll body
    document.body.style.overflow = '';

    // Mostra chat Genesys e sticky cart
    this.showOverlappingElements();

    // Ripristina focus
    if (this.previousActiveElement) {
      this.previousActiveElement.focus();
    }
  }

  setupFocusTrap() {
    this.focusableElements = this.modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (this.focusableElements.length > 0) {
      this.firstFocusable = this.focusableElements[0];
      this.lastFocusable = this.focusableElements[this.focusableElements.length - 1];
    }
  }

  trapFocus(e) {
    if (e.key !== 'Tab') return;

    if (e.shiftKey) {
      // Shift + Tab
      if (document.activeElement === this.firstFocusable) {
        e.preventDefault();
        this.lastFocusable.focus();
      }
    } else {
      // Tab
      if (document.activeElement === this.lastFocusable) {
        e.preventDefault();
        this.firstFocusable.focus();
      }
    }
  }

  // Nasconde il widget chat Genesys e sticky cart
  hideOverlappingElements() {
    const elementsToHide = document.querySelectorAll(`
      .genesys-mxg-launcher-frame,
      .genesys-mxg-conversation,
      [id^="genesys"],
      #stickyAddToCart,
      #stickyAddToCartDesktop
    `);
    elementsToHide.forEach(el => {
      el.dataset.sizeGuideHidden = 'true';
      el.style.setProperty('visibility', 'hidden', 'important');
      el.style.setProperty('opacity', '0', 'important');
    });
  }

  // Mostra gli elementi nascosti
  showOverlappingElements() {
    const hiddenElements = document.querySelectorAll('[data-size-guide-hidden="true"]');
    hiddenElements.forEach(el => {
      delete el.dataset.sizeGuideHidden;
      el.style.removeProperty('visibility');
      el.style.removeProperty('opacity');
    });
  }
}

// Inizializza quando il DOM è pronto (con protezione contro inizializzazioni multiple)
if (!window.sizeGuideModalInitialized) {
  window.sizeGuideModalInitialized = true;
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new SizeGuideModal());
  } else {
    new SizeGuideModal();
  }
}
